import { useState } from 'react'

interface TlvEntry {
  tag: string
  length: string
  value: string
}

function parseTlv(hex: string): TlvEntry[] {
  const clean = hex.replace(/\s/g, '').toUpperCase()
  if (clean.length % 2 !== 0) throw new Error('Hex string must have even length')

  const entries: TlvEntry[] = []
  let i = 0

  while (i < clean.length) {
    // Tag: one or two bytes (if first byte ends in 0x1F, next byte is part of tag)
    let tag = clean.slice(i, i + 2)
    i += 2
    if ((parseInt(tag, 16) & 0x1f) === 0x1f) {
      tag += clean.slice(i, i + 2)
      i += 2
    }

    if (i >= clean.length) break

    // Length
    const lenByte = parseInt(clean.slice(i, i + 2), 16)
    i += 2
    let length: number
    let lengthHex: string

    if (lenByte <= 0x7f) {
      length = lenByte
      lengthHex = lenByte.toString(16).padStart(2, '0').toUpperCase()
    } else {
      const numBytes = lenByte & 0x7f
      lengthHex = clean.slice(i, i + numBytes * 2)
      length = parseInt(lengthHex, 16)
      i += numBytes * 2
    }

    const value = clean.slice(i, i + length * 2)
    i += length * 2

    entries.push({ tag, length: length.toString(), value })
  }

  return entries
}

export default function TlvParser() {
  const [input, setInput] = useState('')
  const [entries, setEntries] = useState<TlvEntry[] | null>(null)
  const [error, setError] = useState('')

  const handleParse = () => {
    setError('')
    setEntries(null)
    try {
      if (!input.trim()) {
        setError('Please enter TLV hex data.')
        return
      }
      const result = parseTlv(input)
      if (result.length === 0) {
        setError('No TLV entries found.')
      } else {
        setEntries(result)
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to parse TLV data')
    }
  }

  return (
    <div className="min-h-screen p-2">
      <h2 className="mb-6 text-left text-2xl font-bold text-gray-900">TLV Parser</h2>

      <div className="space-y-3">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Paste TLV hex data here (e.g. 9F2608A1B2C3D4E5F6A7)..."
          className="h-24 w-full rounded-xl border border-gray-300 bg-white p-4 text-gray-900 outline-none focus:ring-2 focus:ring-indigo-500"
        />

        <button
          onClick={handleParse}
          className="cursor-pointer rounded-xl bg-indigo-600 px-6 py-2 font-semibold text-white shadow transition hover:bg-indigo-700"
        >
          Parse
        </button>

        {error && <div className="font-medium text-red-600">{error}</div>}

        {entries && (
          <div className="mt-2 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden rounded-lg shadow-sm outline outline-1 outline-black/5">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-2 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          Tag
                        </th>
                        <th className="px-3 py-2 text-left text-sm font-semibold text-gray-900">
                          Length
                        </th>
                        <th className="px-3 py-2 text-left text-sm font-semibold text-gray-900">
                          Value
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {entries.map((entry, i) => (
                        <tr key={i}>
                          <td className="py-2 pr-3 pl-4 text-sm font-semibold whitespace-nowrap text-indigo-700 sm:pl-6">
                            {entry.tag}
                          </td>
                          <td className="px-3 py-2 text-sm whitespace-nowrap text-gray-500">
                            {entry.length}
                          </td>
                          <td className="px-3 py-2 text-sm whitespace-nowrap text-gray-900">
                            {entry.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
